import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { ApiService } from './api.service';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { mergeHomePageResponses } from '../utils/home-page-merge';

@Injectable({
  providedIn: 'root'
})

export class HeaderService {

  private apiData = new BehaviorSubject<any>(null);
  public apiData$ = this.apiData.asObservable();
  private bannerData = new BehaviorSubject<any>(null);
  public bannerData$ = this.bannerData.asObservable();
  private newData = new BehaviorSubject<any>(null);
  public newData$ = this.newData.asObservable();
  private webseriesData = new BehaviorSubject<any>(null);
  public webseriesData$ = this.webseriesData.asObservable();
  private recentData = new BehaviorSubject<any>(null);
  public recentData$ = this.recentData.asObservable();
  private home_playlist = new BehaviorSubject<any>(null);
  public home_playlist$ = this.home_playlist.asObservable();
  private liveZoomData = new BehaviorSubject<any>(null);
  public liveZoomData$ = this.liveZoomData.asObservable();
  private albumData = new BehaviorSubject<any>(null);
  public albumData$ = this.albumData.asObservable();
  public homeCategoryData;
  private mainmenuCategoryData = new BehaviorSubject<any>(null);
  public mainmenuCategoryData$ = this.mainmenuCategoryData.asObservable();
  public adminPlaylistData;
  public adminTilePlaylistData;
  public adminShortsPlaylistData;
  /** homeDataScope() the cached home data (streams above + homeCategoryData) was fetched under. */
  private cachedHomeScope: string;
  constructor(private apiService: ApiService) { }

  /** Access flag + language a home response belongs to — both are request headers (see ApiService.headers). */
  homeDataScope(): string {
    return (localStorage.getItem('selectedAccessFlag') || 'kn') + '|' + (localStorage.getItem('currentLanguage') || 'en');
  }

  /**
   * Home data lives in this singleton so revisiting the page paints instantly, but it is only valid for the
   * access flag / language it was fetched under. HomeComponent calls this before reading it: after an access
   * switch (header -> /loading -> back re-creates the page) or a language switch the previous scope's data is
   * dropped, so the page shows skeletons and then the fresh content instead of the previous flag's sections.
   */
  dropStaleHomeData() {
    if (this.cachedHomeScope && this.cachedHomeScope !== this.homeDataScope()) {
      this.clearHomeData();
    }
  }

  clearHomeData() {
    this.cachedHomeScope = undefined;
    this.homeCategoryData = undefined;
    this.adminPlaylistData = undefined;
    this.adminTilePlaylistData = undefined;
    this.adminShortsPlaylistData = undefined;
    [this.webseriesData, this.bannerData, this.newData, this.recentData, this.home_playlist, this.liveZoomData]
      .forEach(stream => stream.next(null));
  }

  fetchData() {
    return this.apiService.callGetAPI('v2/footer', environment.commonApiUrl).pipe(map((data) => {
      return data;
    }));
  }
  setData(data) {
    this.apiData.next(data);
  }

  /**
   * The home dashboard is served by two APIs (they replace the retired `home_page_dashboard`):
   *  - static_home_page : CDN-cacheable content (banners, new, playlists, tile playlists, shorts, ...)
   *  - live_home_page   : live banners / live videos / live zoom + the signed-in user's recent videos
   * `query` is forwarded verbatim to static_home_page (e.g. '?displaypage=homepage&page=2').
   */
  fetchStaticHomePage(query: string = ''): Observable<any> {
    return this.apiService.callGetAPI('v2/static_home_page' + query);
  }

  fetchLiveHomePage(): Observable<any> {
    return this.apiService.callGetAPI('v2/live_home_page');
  }

  /**
   * Calls both home APIs in parallel and emits ONE merged envelope (`{ statusCode, error, message, response }`)
   * following the rules in core/utils/home-page-merge.ts. A failing API is reported through `failureResponse`
   * and skipped, so the page still renders whatever half succeeded; `null` is emitted only when both failed.
   */
  fetchHomeDashboard(query: string = ''): Observable<any> {
    const tolerant = (request$: Observable<any>) => request$.pipe(
      catchError(error => {
        this.apiService.failureResponse(error, true);
        return of(null);
      })
    );
    return forkJoin([tolerant(this.fetchStaticHomePage(query)), tolerant(this.fetchLiveHomePage())]).pipe(
      map(([staticData, liveData]) => mergeHomePageResponses(staticData, liveData))
    );
  }

  /**
   * Pushes a merged home-dashboard envelope (see fetchHomeDashboard) into the shared home streams.
   * `scope` is homeDataScope() from when the request was made: a response that arrives after the access flag /
   * language changed belongs to the old scope and is ignored, so the streams never hold another flag's data.
   */
  setHomeDashboardData(data, scope: string = this.homeDataScope()) {
    if (!data || data['statusCode'] !== 200 || data['error'] !== false || scope !== this.homeDataScope()) {
      return;
    }
    this.cachedHomeScope = scope;
    const response = data['response'] || {};
    this.webseriesData.next(response.webseries);
    this.bannerData.next(response.banner_new);
    this.newData.next(response.new);
    this.recentData.next(response.recent);
    this.home_playlist.next(response.home_playlist);
    this.liveZoomData.next(response.live_zoom);
  }

  fetchBanner() {
    this.fetchHomeDashboard().subscribe(data => this.setHomeDashboardData(data));
  }

  fetchMainmenuCategories() {
    return this.apiService.callGetAPI('v2/menu-categories', environment.commonApiUrl).pipe(map((data) => {
      return data;
    }));
  }

  /** Caches the home categories (`v2/home_page`); `scope` semantics as in setHomeDashboardData. */
  setHomeCategory(data, scope: string = this.homeDataScope()) {
    if (scope !== this.homeDataScope()) {
      return;
    }
    this.cachedHomeScope = scope;
    this.homeCategoryData = data;
  }

  getHomeCategory() {
    return this.homeCategoryData;
  }

  setMainMenuCategory(data) {
    this.mainmenuCategoryData.next(data);
  }

  fetchAlbumListData() {
    const apiUrl = 'v2/browse?search=' + 'all' + '&page=' + 1;
    return this.apiService.callGetAPI(apiUrl)
      .subscribe(data => {
        this.albumData.next(data);
      },
        error => {
          this.apiService.failureResponse(error);
        });
  }
  setAdminPlaylistData(data) {
    this.adminPlaylistData = data;
  }

  getAdminPlaylistsData() {
    return this.getAdminPlaylistsData;
  }
  setAdminTilePlaylistData(data) {
    this.adminTilePlaylistData = data;
  }

  getAdminTilePlaylistsData() {
    return this.getAdminTilePlaylistsData;
  }

  setAdminShortsPlaylistData(data) {
    this.adminShortsPlaylistData = data;
  }

  getAdminShortsPlaylistsData() {
    return this.getAdminShortsPlaylistsData;
  }
}
